import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Slider,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import {
  Group as GroupIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as CopyIcon,
  MyLocation as CheckInIcon,
  LocationOn as LocationIcon,
  MoreVert as MoreIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Star as OwnerIcon,
  ExitToApp as LeaveIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Place as PlaceIcon,
  AccessTime as TimesheetIcon,
  GpsOff as GpsOffIcon,
  GpsFixed as GpsOnIcon,
  Security as PrivacyIcon,
  Share as ShareIcon,
  QrCode as QrCodeIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { crewService, Crew, CrewMember, Meeting, TimesheetSubmission, LocationBroadcast } from '../services/crewService';
import { TimesheetPanel } from './TimesheetPanel';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { useTranslation } from '../i18n/translations';

interface CrewPanelProps {
  onNavigateToLocation?: (lat: number, lng: number, name: string) => void;
  currentLocation?: { lat: number; lng: number } | null;
}

const CrewPanel: React.FC<CrewPanelProps> = ({ onNavigateToLocation, currentLocation }) => {
  const { t } = useTranslation();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [members, setMembers] = useState<CrewMember[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [userRole, setUserRole] = useState<string>('member');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useAutoDismiss(error, () => setError(null));
  useAutoDismiss(success, () => setSuccess(null), 3000);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [expandedCrew, setExpandedCrew] = useState<string | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [timesheetSubmitDialogOpen, setTimesheetSubmitDialogOpen] = useState(false);
  const [memberLocationsVisible, setMemberLocationsVisible] = useState(false);
  
  // Form states
  const [newCrewName, setNewCrewName] = useState('');
  const [newCrewDescription, setNewCrewDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const [meetingRadius, setMeetingRadius] = useState(500);
  
  // Lead settings form
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [timesheetDay, setTimesheetDay] = useState('friday');
  
  // QR code state
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Member locations
  const [memberLocations, setMemberLocations] = useState<LocationBroadcast[]>([]);
  
  // Timesheet submission form
  const [timesheetNotes, setTimesheetNotes] = useState('');
  const [_timesheetHistory, _setTimesheetHistory] = useState<TimesheetSubmission[]>([]);
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMember, setMenuMember] = useState<CrewMember | null>(null);
  
  // Tab state for Crews vs Timesheet
  const [activeTab, setActiveTab] = useState<'crews' | 'timesheet'>('crews');

  // GPS Privacy State - Default OFF for BYOD workers privacy
  // This controls whether your location is shared with crew members
  // Navigation GPS (for driving) is always available separately
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(() => {
    const saved = localStorage.getItem('crew-location-sharing');
    return saved === 'true'; // Default to false (OFF) for privacy
  });

  // Persist GPS sharing preference
  const handleLocationSharingToggle = (enabled: boolean) => {
    setLocationSharingEnabled(enabled);
    localStorage.setItem('crew-location-sharing', enabled.toString());
    console.log('[CrewPanel] Location sharing:', enabled ? 'ENABLED' : 'DISABLED');
    // TODO: Update location sharing status on server
  };

  const loadCrews = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[CrewPanel] Loading crews...');
      const { crews: loadedCrews } = await crewService.getMyCrews();
      console.log('[CrewPanel] Loaded crews:', loadedCrews);
      setCrews(loadedCrews);
    } catch (err) {
      console.error('[CrewPanel] Load crews error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load crews');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCrewDetails = useCallback(async (crewId: string) => {
    try {
      console.log('[CrewPanel] Loading crew details:', crewId);
      const details = await crewService.getCrewDetails(crewId);
      console.log('[CrewPanel] Crew details:', details);
      setSelectedCrew(details.crew);
      setMembers(details.members);
      setMeetings(details.meetings);
      setUserRole(details.userRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load crew details');
    }
  }, []);

  useEffect(() => {
    loadCrews();
  }, [loadCrews]);

  const handleCreateCrew = async () => {
    if (!newCrewName.trim()) {
      console.log('[CrewPanel] Create aborted: empty crew name');
      return;
    }
    
    try {
      setLoading(true);
      console.log('[CrewPanel] Creating crew:', { name: newCrewName, desc: newCrewDescription });
      const result = await crewService.createCrew(newCrewName.trim(), newCrewDescription.trim() || undefined);
      console.log('[CrewPanel] Crew created successfully:', result);
      setSuccess(result.message);
      setCreateDialogOpen(false);
      setNewCrewName('');
      setNewCrewDescription('');
      loadCrews();
    } catch (err) {
      console.error('[CrewPanel] Create crew error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create crew');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCrew = async () => {
    if (!inviteCode.trim()) return;
    
    try {
      setLoading(true);
      const result = await crewService.joinCrew(inviteCode.trim());
      setSuccess(result.message);
      setJoinDialogOpen(false);
      setInviteCode('');
      loadCrews();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join crew');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInviteCode = async (code: string) => {
    const copied = await crewService.copyInviteCode(code);
    if (copied) {
      setSuccess(t('inviteCodeCopied'));
    }
  };

  const handleLeaveCrew = async (crewId: string) => {
    if (!confirm(t('confirmLeave'))) return;
    
    try {
      await crewService.leaveCrew(crewId);
      setSuccess(t('leftCrew'));
      loadCrews();
      setExpandedCrew(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave crew');
    }
  };

  const handleDeleteCrew = async (crewId: string) => {
    if (!confirm(t('confirmDelete'))) return;
    
    try {
      await crewService.deleteCrew(crewId);
      setSuccess(t('crewDeleted'));
      loadCrews();
      setExpandedCrew(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete crew');
    }
  };

  const handleCreateMeeting = async () => {
    if (!selectedCrew || !currentLocation) return;
    
    try {
      setLoading(true);
      const result = await crewService.createMeeting({
        crewId: selectedCrew.id,
        name: meetingName.trim() || 'Meeting Point',
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
        proximityRadiusMeters: meetingRadius,
      });
      setSuccess(result.message);
      setMeetingDialogOpen(false);
      setMeetingName('');
      loadCrewDetails(selectedCrew.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (meeting: Meeting) => {
    if (!currentLocation) {
      setError(t('locationNotAvailable'));
      return;
    }
    
    try {
      const result = await crewService.checkIn(meeting.id, currentLocation.lat, currentLocation.lng);
      setSuccess(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check in');
    }
  };

  const handleMemberMenuOpen = (event: React.MouseEvent<HTMLElement>, member: CrewMember) => {
    setMenuAnchor(event.currentTarget);
    setMenuMember(member);
  };

  const handleMemberMenuClose = () => {
    setMenuAnchor(null);
    setMenuMember(null);
  };

  const handleUpdateMemberRole = async (role: 'admin' | 'member') => {
    if (!menuMember) return;
    
    try {
      await crewService.updateMemberRole(menuMember.id, role);
      setSuccess(t('roleUpdated', role));
      handleMemberMenuClose();
      if (selectedCrew) loadCrewDetails(selectedCrew.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemoveMember = async () => {
    if (!menuMember) return;
    if (!confirm(t('confirmRemove', menuMember.telegram_username || 'this member'))) return;
    
    try {
      await crewService.removeMember(menuMember.id);
      setSuccess(t('memberRemoved'));
      handleMemberMenuClose();
      if (selectedCrew) loadCrewDetails(selectedCrew.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  // Share invite
  const handleShareInvite = async (crew: Crew) => {
    try {
      const shared = await crewService.shareInvite(crew);
      if (shared) {
        setSuccess(t('inviteShared'));
      }
    } catch (err) {
      setError(t('failedToShare'));
    }
  };

  // Show QR code
  const handleShowQR = async (crew: Crew) => {
    setSelectedCrew(crew);
    const qrUrl = await crewService.generateInviteQR(crew.invite_code);
    setQrCodeUrl(qrUrl);
    setQrDialogOpen(true);
  };

  // Open settings dialog
  const handleOpenSettings = (crew: Crew) => {
    setSelectedCrew(crew);
    setLeadEmail(crew.lead_email || '');
    setLeadName(crew.lead_name || '');
    setCompanyName(crew.company_name || '');
    setAutoSubmit(crew.auto_submit_timesheet === 1);
    setTimesheetDay(crew.timesheet_day || 'friday');
    setSettingsDialogOpen(true);
  };

  // Save lead settings
  const handleSaveSettings = async () => {
    if (!selectedCrew) return;
    
    try {
      setLoading(true);
      await crewService.updateCrewSettings(selectedCrew.id, {
        leadEmail: leadEmail.trim() || undefined,
        leadName: leadName.trim() || undefined,
        companyName: companyName.trim() || undefined,
        autoSubmitTimesheet: autoSubmit,
        timesheetDay
      });
      setSuccess(t('settingsSaved'));
      setSettingsDialogOpen(false);
      loadCrews();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Load member locations
  const loadMemberLocations = async (crewId: string) => {
    try {
      const { locations } = await crewService.getCrewLocations(crewId);
      setMemberLocations(locations);
    } catch (err) {
      console.error('Failed to load member locations:', err);
    }
  };

  // Submit weekly timesheet
  const handleSubmitTimesheet = async () => {
    if (!selectedCrew) return;
    
    // Calculate week range (Mon-Sun)
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(now.setDate(diff));
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    try {
      setLoading(true);
      const result = await crewService.submitTimesheet(selectedCrew.id, {
        weekStart: weekStart.toISOString().split('T')[0],
        weekEnd: weekEnd.toISOString().split('T')[0],
        totalHours: 40, // TODO: Get from actual time entries
        notes: timesheetNotes.trim() || undefined
      });
      setSuccess(result.message);
      setTimesheetSubmitDialogOpen(false);
      setTimesheetNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit timesheet');
    } finally {
      setLoading(false);
    }
  };

  // Load timesheet history (for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _loadTimesheetHistory = async (crewId: string) => {
    try {
      const { submissions } = await crewService.getTimesheetHistory(crewId);
      _setTimesheetHistory(submissions);
    } catch (err) {
      console.error('Failed to load timesheet history:', err);
    }
  };
  void _loadTimesheetHistory; // Silence unused warning

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <OwnerIcon sx={{ color: '#ffc107' }} />;
      case 'admin': return <AdminIcon sx={{ color: '#2196f3' }} />;
      default: return <PersonIcon />;
    }
  };

  const toggleCrewExpand = (crewId: string) => {
    if (expandedCrew === crewId) {
      setExpandedCrew(null);
      setSelectedCrew(null);
    } else {
      setExpandedCrew(crewId);
      loadCrewDetails(crewId);
    }
  };

  if (loading && crews.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CyberSpinner size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Tab Navigation */}
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          value="crews" 
          label={t('myCrews')} 
          icon={<GroupIcon />} 
          iconPosition="start"
          sx={{ minHeight: 48 }}
        />
        <Tab 
          value="timesheet" 
          label={t('timesheet')} 
          icon={<TimesheetIcon />} 
          iconPosition="start"
          sx={{ minHeight: 48 }}
        />
      </Tabs>

      {/* Timesheet Tab */}
      {activeTab === 'timesheet' && (
        <TimesheetPanel 
          crewId={selectedCrew?.id} 
          isAdmin={userRole === 'owner' || userRole === 'admin'}
        />
      )}

      {/* Crews Tab */}
      {activeTab === 'crews' && (
      <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon /> {t('myCrews')}
        </Typography>
        <Box>
          <Tooltip title={t('refresh')}>
            <IconButton onClick={loadCrews} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* GPS Privacy Toggle - Default OFF for BYOD workers */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: locationSharingEnabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(100, 116, 139, 0.1)',
          border: locationSharingEnabled ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(100, 116, 139, 0.2)',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {locationSharingEnabled ? (
            <GpsOnIcon sx={{ color: '#22c55e', fontSize: 28 }} />
          ) : (
            <GpsOffIcon sx={{ color: '#64748b', fontSize: 28 }} />
          )}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PrivacyIcon sx={{ fontSize: 16 }} />
              {t('locationSharing')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {locationSharingEnabled 
                ? t('locationVisible') 
                : t('locationPrivate')}
            </Typography>
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={locationSharingEnabled}
              onChange={(e) => handleLocationSharingToggle(e.target.checked)}
              color="success"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#22c55e',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#22c55e',
                },
              }}
            />
          }
          label=""
          sx={{ m: 0 }}
        />
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          size="small"
          fullWidth
        >
          {t('createCrew')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={() => setJoinDialogOpen(true)}
          size="small"
          fullWidth
        >
          {t('joinCrew')}
        </Button>
      </Box>

      {/* Crews List */}
      {crews.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
          <GroupIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('notPartOfCrews')}
            <br />
            {t('createOrJoinCrew')}
          </Typography>
        </Paper>
      ) : (
        <List sx={{ p: 0 }}>
          {crews.map((crew) => (
            <Paper 
              key={crew.id} 
              sx={{ 
                mb: 1, 
                overflow: 'hidden',
                boxShadow: '0 6px 16px rgba(96, 165, 250, 0.15)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                transform: 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 12px 28px rgba(96, 165, 250, 0.3)'
                }
              }}
            >
              <ListItem
                component="div"
                onClick={() => toggleCrewExpand(crew.id)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={crew.active_meetings}
                    color="primary"
                    invisible={!crew.active_meetings}
                  >
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <GroupIcon />
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={crew.name}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={`${crew.member_count || 1} ${crew.member_count === 1 ? t('member') : t('members')}`}
                        sx={{ fontSize: '0.7rem' }}
                      />
                      <Chip
                        size="small"
                        icon={getRoleIcon(crew.user_role || 'member')}
                        label={crew.user_role || 'member'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
                <ListItemSecondaryAction>
                  {expandedCrew === crew.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemSecondaryAction>
              </ListItem>

              <Collapse in={expandedCrew === crew.id}>
                <Divider />
                <Box sx={{ p: 2 }}>
                  {/* Invite Code */}
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('inviteCode')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                        {crew.invite_code}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyInviteCode(crew.invite_code)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleShareInvite(crew)} color="primary">
                        <ShareIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleShowQR(crew)} color="secondary">
                        <QrCodeIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Lead Settings (Owner/Admin only) */}
                  {['owner', 'admin'].includes(userRole) && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleOpenSettings(crew)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {t('leadTimesheetSettings')}
                      </Button>
                      {crew.lead_email && (
                        <Chip
                          size="small"
                          icon={<EmailIcon />}
                          label={`Lead: ${crew.lead_name || crew.lead_email}`}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Active Meetings */}
                  {meetings.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon fontSize="small" /> {t('activeMeetings')}
                      </Typography>
                      {meetings.map((meeting) => (
                        <Card 
                          key={meeting.id} 
                          sx={{ 
                            mb: 1, 
                            bgcolor: 'background.default',
                            boxShadow: '0 6px 16px rgba(251, 191, 36, 0.2)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            transform: 'scale(1)',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: '0 12px 28px rgba(251, 191, 36, 0.35)'
                            }
                          }}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="subtitle2">{meeting.name}</Typography>
                            {meeting.location_name ? (
                              <Typography variant="caption" color="text.secondary">
                                {meeting.location_name}
                              </Typography>
                            ) : (
                              <Chip
                                size="small"
                                label={`📍 ${meeting.latitude.toFixed(4)}, ${meeting.longitude.toFixed(4)}`}
                                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${meeting.latitude},${meeting.longitude}`, '_blank')}
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: 'rgba(251, 191, 36, 0.15)',
                                  color: '#fbbf24',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20,
                                  '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
                                }}
                              />
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Chip
                                size="small"
                                icon={<CheckCircleIcon />}
                                label={t('checkIns', meeting.check_in_count || 0)}
                                sx={{ fontSize: '0.65rem' }}
                              />
                              <Chip
                                size="small"
                                label={t('radiusMeters', meeting.proximity_radius_meters)}
                                sx={{ fontSize: '0.65rem' }}
                              />
                            </Box>
                          </CardContent>
                          <CardActions sx={{ pt: 0 }}>
                            <Button
                              size="small"
                              startIcon={<CheckInIcon />}
                              onClick={() => handleCheckIn(meeting)}
                            >
                              {t('checkIn')}
                            </Button>
                            {onNavigateToLocation && (
                              <Button
                                size="small"
                                startIcon={<PlaceIcon />}
                                onClick={() => onNavigateToLocation(meeting.latitude, meeting.longitude, meeting.name)}
                              >
                                {t('navigate')}
                              </Button>
                            )}
                          </CardActions>
                        </Card>
                      ))}
                    </Box>
                  )}

                  {/* Create Meeting Button */}
                  {['owner', 'admin'].includes(userRole) && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setSelectedCrew(crew);
                        setMeetingDialogOpen(true);
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                      disabled={!currentLocation}
                    >
                      {t('setMeetingPoint')}
                    </Button>
                  )}

                  {/* Members List */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('members')} ({members.length})
                  </Typography>
                  <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                    {members.map((member) => (
                      <ListItem key={member.id}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {getRoleIcon(member.role)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.telegram_username || `User ${member.telegram_user_id.slice(-4)}`}
                          secondary={member.role}
                        />
                        {userRole === 'owner' && member.role !== 'owner' && (
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMemberMenuOpen(e, member)}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>

                  {/* Actions */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* View Member Locations */}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      onClick={() => {
                        setSelectedCrew(crew);
                        loadMemberLocations(crew.id);
                        setMemberLocationsVisible(true);
                      }}
                    >
                      Live Locations
                    </Button>
                    
                    {/* Submit Timesheet */}
                    {['owner', 'admin'].includes(crew.user_role || '') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<SendIcon />}
                        onClick={() => {
                          setSelectedCrew(crew);
                          setTimesheetSubmitDialogOpen(true);
                        }}
                      >
                        Submit Timesheet
                      </Button>
                    )}
                    
                    {crew.user_role === 'owner' ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteCrew(crew.id)}
                      >
                        Delete
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<LeaveIcon />}
                        onClick={() => handleLeaveCrew(crew.id)}
                      >
                        Leave
                      </Button>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </List>
      )}

      {/* Create Crew Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create New Crew</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('crewName')}
            fullWidth
            value={newCrewName}
            onChange={(e) => setNewCrewName(e.target.value)}
            placeholder="e.g., Night Shift Team"
          />
          <TextField
            margin="dense"
            label={t('description')}
            fullWidth
            multiline
            rows={2}
            value={newCrewDescription}
            onChange={(e) => setNewCrewDescription(e.target.value)}
            placeholder={t('descriptionPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleCreateCrew} variant="contained" disabled={!newCrewName.trim()}>
            {t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Join Crew Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('joinCrew')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('inviteCode')}
            fullWidth
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder={t('inviteCodePlaceholder')}
            inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 4, textAlign: 'center' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleJoinCrew} variant="contained" disabled={!inviteCode.trim()}>
            {t('join')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Meeting Dialog */}
      <Dialog open={meetingDialogOpen} onClose={() => setMeetingDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('createMeetingPoint')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('meetingLocationDesc')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('meetingName')}
            fullWidth
            value={meetingName}
            onChange={(e) => setMeetingName(e.target.value)}
            placeholder={t('meetingNamePlaceholder')}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              {t('checkInRadius', meetingRadius)}
            </Typography>
            <Slider
              value={meetingRadius}
              onChange={(_, value) => setMeetingRadius(value as number)}
              min={100}
              max={2000}
              step={100}
              marks={[
                { value: 100, label: '100m' },
                { value: 500, label: '500m' },
                { value: 1000, label: '1km' },
                { value: 2000, label: '2km' },
              ]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeetingDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleCreateMeeting} variant="contained">
            {t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMemberMenuClose}
      >
        {menuMember?.role === 'member' && (
          <MenuItem onClick={() => handleUpdateMemberRole('admin')}>
            <AdminIcon sx={{ mr: 1 }} /> {t('makeAdmin')}
          </MenuItem>
        )}
        {menuMember?.role === 'admin' && (
          <MenuItem onClick={() => handleUpdateMemberRole('member')}>
            <PersonIcon sx={{ mr: 1 }} /> {t('removeAdmin')}
          </MenuItem>
        )}
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> {t('removeFromCrew')}
        </MenuItem>
      </Menu>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ textAlign: 'center' }}>
          <QrCodeIcon sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
          {t('scanToJoin')}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
          {qrCodeUrl && (
            <Box sx={{ mb: 2 }}>
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                style={{ width: 200, height: 200, borderRadius: 8 }}
              />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('orShareCode')}
          </Typography>
          <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 4 }}>
            {selectedCrew?.invite_code}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>{t('close')}</Button>
          <Button 
            variant="contained" 
            startIcon={<ShareIcon />}
            onClick={() => selectedCrew && handleShareInvite(selectedCrew)}
          >
            {t('share')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('leadTimesheetSettings')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('timesheetSettingsDesc')}
          </Typography>
          
          <TextField
            margin="dense"
            label={t('leadName')}
            fullWidth
            value={leadName}
            onChange={(e) => setLeadName(e.target.value)}
            placeholder={t('leadNamePlaceholder')}
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            margin="dense"
            label={t('leadEmail')}
            type="email"
            fullWidth
            value={leadEmail}
            onChange={(e) => setLeadEmail(e.target.value)}
            placeholder={t('leadEmailPlaceholder')}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            margin="dense"
            label={t('companyName')}
            fullWidth
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t('companyNamePlaceholder')}
            InputProps={{
              startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <Divider sx={{ my: 2 }} />
          
          <FormControlLabel
            control={
              <Switch
                checked={autoSubmit}
                onChange={(e) => setAutoSubmit(e.target.checked)}
                color="primary"
              />
            }
            label={t('autoSubmitTimesheet')}
          />
          
          {autoSubmit && (
            <TextField
              select
              margin="dense"
              label={t('submitOn')}
              fullWidth
              value={timesheetDay}
              onChange={(e) => setTimesheetDay(e.target.value)}
              InputProps={{
                startAdornment: <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            >
              <MenuItem value="monday">{t('monday')}</MenuItem>
              <MenuItem value="tuesday">{t('tuesday')}</MenuItem>
              <MenuItem value="wednesday">{t('wednesday')}</MenuItem>
              <MenuItem value="thursday">{t('thursday')}</MenuItem>
              <MenuItem value="friday">{t('friday')}</MenuItem>
              <MenuItem value="saturday">{t('saturday')}</MenuItem>
              <MenuItem value="sunday">{t('sunday')}</MenuItem>
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleSaveSettings} variant="contained">
            {t('saveSettings')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Locations Dialog */}
      <Dialog 
        open={memberLocationsVisible} 
        onClose={() => setMemberLocationsVisible(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>
          <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
          {t('liveMemberLocations')}
          <IconButton 
            size="small" 
            onClick={() => selectedCrew && loadMemberLocations(selectedCrew.id)}
            sx={{ ml: 1 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {memberLocations.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('noMembersSharedLocation')}
            </Alert>
          ) : (
            <List>
              {memberLocations.map((loc) => (
                <ListItem 
                  key={loc.telegram_user_id}
                  secondaryAction={
                    <Chip
                      size="small"
                      icon={<GpsOnIcon />}
                      label={new Date(loc.recorded_at).toLocaleTimeString()}
                      color="success"
                      variant="outlined"
                    />
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={loc.telegram_username || `User ${loc.telegram_user_id.slice(-4)}`}
                    secondary={
                      <Chip
                        size="small"
                        label={`📍 ${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`}
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`, '_blank')}
                        sx={{ cursor: 'pointer', mt: 0.5 }}
                      />
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberLocationsVisible(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Submit Timesheet Dialog */}
      <Dialog 
        open={timesheetSubmitDialogOpen} 
        onClose={() => setTimesheetSubmitDialogOpen(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>
          <SendIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
          {t('submitWeeklyTimesheet')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('timesheetSubmitDesc', selectedCrew?.lead_email || t('noEmailConfigured'))}
          </Alert>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('week')}: {(() => {
              const now = new Date();
              const day = now.getDay();
              const diff = now.getDate() - day + (day === 0 ? -6 : 1);
              const weekStart = new Date(now.setDate(diff));
              const weekEnd = new Date(weekStart);
              weekEnd.setDate(weekEnd.getDate() + 6);
              return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
            })()}
          </Typography>
          
          <TextField
            margin="dense"
            label={t('notesOptional')}
            fullWidth
            multiline
            rows={3}
            value={timesheetNotes}
            onChange={(e) => setTimesheetNotes(e.target.value)}
            placeholder={t('notesPlaceholder')}
          />
          
          {!selectedCrew?.lead_email && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('noLeadEmailWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimesheetSubmitDialogOpen(false)}>{t('cancel')}</Button>
          <Button 
            onClick={handleSubmitTimesheet} 
            variant="contained" 
            color="success"
            startIcon={<SendIcon />}
            disabled={!selectedCrew?.lead_email}
          >
            {t('submitToLead')}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CrewPanel;
